// lib/time.ts
//
// Small helpers for relative time and MYR currency formatting.
// Pure functions so they can be imported anywhere (client and server).

import { LOCALE_DATE, LOCALE_NUMBER, TZ } from "@/lib/config/locale";

const MS = {
  minute: 60_000,
  hour: 60 * 60_000,
  day: 24 * 60 * 60_000,
  week: 7 * 24 * 60 * 60_000,
};
const MYT_OFFSET_MS = 8 * 60 * 60_000;

const MYT_DATE_FORMATTER = new Intl.DateTimeFormat("en-CA", {
  timeZone: TZ,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

const MYT_HOUR_FORMATTER = new Intl.DateTimeFormat("en-CA", {
  timeZone: TZ,
  hour: "2-digit",
  hourCycle: "h23",
});

const MYT_DAY_LABEL_FORMATTER = new Intl.DateTimeFormat(LOCALE_DATE, {
  timeZone: TZ,
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

const MYT_TIME_FORMATTER = new Intl.DateTimeFormat("en-GB", {
  timeZone: TZ,
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});

function partValue(
  parts: Intl.DateTimeFormatPart[],
  type: string,
): string {
  return parts.find((part) => part.type === type)?.value ?? "";
}

function toDate(ts: Date | string | number): Date {
  return ts instanceof Date ? ts : new Date(ts);
}

/** Returns today's calendar date in MYT (UTC+8) as YYYY-MM-DD. */
export function todayIsoMyt(now: Date = new Date()): string {
  return new Date(now.getTime() + MYT_OFFSET_MS).toISOString().slice(0, 10);
}

/**
 * Returns today's calendar date in UTC as YYYY-MM-DD. Only for places that
 * genuinely want the UTC day (a UTC-stamped key, a filename); anything a
 * person reads or compares with a Malaysia date wants todayIsoMyt().
 */
export function todayIsoUtc(now: Date = new Date()): string {
  return now.toISOString().slice(0, 10);
}

/**
 * The calendar day after an ISO date — pure string math on the date itself, so
 * it is safe on the client (no device clock, no zone) and lands correctly on
 * month, year and leap-day boundaries.
 */
export function nextIsoDay(iso: string): string {
  const [year, month, day] = iso.split("-").map(Number);
  return new Date(Date.UTC(year!, month! - 1, day! + 1)).toISOString().slice(0, 10);
}

/**
 * Return the MYT calendar date (YYYY-MM-DD) of a timestamp.
 * Accepts a Date, ISO string (with or without offset), or epoch ms.
 * Uses Asia/Kuala_Lumpur via Intl, NOT a naive +8h shift, so daylight-saving
 * regions and historical dates resolve correctly.
 */
export function mytDateOf(ts: Date | string | number): string {
  const parts = MYT_DATE_FORMATTER.formatToParts(toDate(ts));
  const year = partValue(parts, "year");
  const month = partValue(parts, "month");
  const day = partValue(parts, "day");
  return `${year}-${month}-${day}`;
}

/**
 * Return the MYT hour (0-23) of a timestamp. Same input/contract as mytDateOf.
 * Use this anywhere code reads an `occurred_at`-style timestamptz column.
 */
export function mytHourOf(ts: Date | string | number): number {
  const parts = MYT_HOUR_FORMATTER.formatToParts(toDate(ts));
  return Number(partValue(parts, "hour"));
}

/**
 * The MYT calendar day of a timestamp as "29/09/2026" (dd/mm/yyyy) — the zoned
 * twin of `toLocaleDateString("en-GB")`, which prints the SERVER's (UTC) day.
 */
export function mytDayLabel(ts: Date | string | number): string {
  return MYT_DAY_LABEL_FORMATTER.format(toDate(ts));
}

/**
 * Format an ISO timestamp as an HH:MM:SS time-of-day stamp in MYT.
 * Used for the live-refresh "Updated HH:MM:SS" marker. Pure — safe in client
 * and server components.
 */
export function formatUpdatedStamp(iso: string): string {
  return MYT_TIME_FORMATTER.format(new Date(iso));
}

const ZONELESS_LOCAL = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,3})?)?$/;

/**
 * Turn a picker value into the instant to store. `<input type="datetime-local">`
 * sends "2026-09-30T15:00" with no zone; servers and the database run in UTC,
 * so stored raw it lands 8 hours late (the hiring interview bug, #2517). A
 * zone-less value is Kuala Lumpur wall-clock time (UTC+8, no daylight saving);
 * a value that already names its zone keeps that instant. Null for blank or
 * unreadable input.
 */
export function mytLocalToIso(value: string): string | null {
  const raw = value.trim();
  if (!raw) return null;
  const ms = Date.parse(ZONELESS_LOCAL.test(raw) ? `${raw}+08:00` : raw);
  return Number.isNaN(ms) ? null : new Date(ms).toISOString();
}

/**
 * The other direction: a stored instant as the "YYYY-MM-DDTHH:mm" Kuala Lumpur
 * wall clock a datetime-local input expects. `iso.slice(0, 16)` shows the UTC
 * clock instead, and re-saving it shifts the time again. Empty when missing.
 */
export function mytLocalInputOf(iso: string | null | undefined): string {
  if (!iso) return "";
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return "";
  return new Date(ms + MYT_OFFSET_MS).toISOString().slice(0, 16);
}

/** Returns the MYT calendar date N days before `now`, as YYYY-MM-DD. */
export function daysAgoIsoMyt(days: number, now: Date = new Date()): string {
  return new Date(now.getTime() + MYT_OFFSET_MS - days * MS.day)
    .toISOString()
    .slice(0, 10);
}

/** "just now", "3 min ago", "5 hr ago", "2 d ago", "3 wk ago", or an absolute date. */
export function relativeTime(from: string | Date, now: Date = new Date()): string {
  const then = typeof from === "string" ? new Date(from) : from;
  if (Number.isNaN(then.getTime())) return "—";
  const delta = now.getTime() - then.getTime();
  if (delta < 45_000) return "just now";
  if (delta < MS.hour) return `${Math.round(delta / MS.minute)} min ago`;
  if (delta < MS.day) return `${Math.round(delta / MS.hour)} hr ago`;
  if (delta < MS.week) return `${Math.round(delta / MS.day)} d ago`;
  if (delta < 4 * MS.week) return `${Math.round(delta / MS.week)} wk ago`;
  return then.toLocaleDateString(LOCALE_DATE, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

const MYR = new Intl.NumberFormat(LOCALE_NUMBER, {
  style: "currency",
  currency: "MYR",
  maximumFractionDigits: 2,
});

/** Format a number as MYR currency (Malaysian Ringgit). */
export function formatMYR(amount: number): string {
  return MYR.format(amount);
}
